import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

// Dedicated landing for "lelulaatikko" (1 000 /mo, KDI 12). Until now only the
// toy-box product page ranked (pos ~34) — a category page matches the
// comparison-shopping intent behind the head term.
const SLUG = "lelulaatikko";
const H1 = "Lelulaatikko & lelukori — siisti lastenhuone lapsen ehdoilla";
const TITLE = "Lelulaatikko & lelukori — puinen säilytys | Montessori24";
const DESC =
  "Puinen lelulaatikko ja lelukori lapsen korkeudella — taapero siivoaa itse. Koivuvaneri, ilmainen toimitus 3–7 pv, 30 pv palautusetu.";

const FAQS = [
  {
    q: "Mikä on hyvä lelulaatikko lapselle?",
    a: "Hyvä lelulaatikko on matala, kevyt avata ja lapsen korkeudella oleva säilytysratkaisu, johon taapero yltää itse. Puinen lelulaatikko kestää vuosien käyttöä, ja pyöreät kulmat sekä lapsiturvallinen pintakäsittely (EN71) tekevät siitä turvallisen arjessa.",
  },
  {
    q: "Lelulaatikko vai lelukori — kumpi kannattaa valita?",
    a: "Lelulaatikko sopii suuremmalle lelumäärälle ja piilottaa sälää näkyvistä, kun taas lelukori on kevyt ja helppo kantaa huoneesta toiseen. Moni perhe yhdistää: laatikko säilyttää pääosan leluista ja pari koria pyörittää viikon suosikkeja.",
  },
  {
    q: "Minkä ikäinen lapsi osaa siivota lelut itse?",
    a: "Jo 1,5–2-vuotias oppii laittamaan lelut laatikkomallia noudattaen, kun säilytys on lapsen korkeudella ja paikat ovat selkeät. 3–4-vuotias hoitaa siivoamisen itsenäisesti, kun jokaisella leluryhmällä on oma paikkansa.",
  },
  {
    q: "Miten lelulaatikko tukee Montessori-ajattelua?",
    a: "Montessorissa ympäristö ohjaa käyttäytymistä: kun lelut ovat lapsen ulottuvilla ja jokaisella on oma paikkansa, lapsi sekä valitsee leikkinsä että siivoaa jälkensä ilman jatkuvaa aikuisen apua. Se kasvattaa vastuunkantoa ja järjestystajua.",
  },
  {
    q: "Mahtuuko lelulaatikko pieneen lastenhuoneeseen?",
    a: "Kyllä. Tyypillinen lelulaatikko on 50–80 cm leveä ja mahtuu sängyn viereen tai hyllyn alle. Moni malli toimii myös penkkinä tai ylimääräisenä pöytänä, joten sama kaluste hoitaa kahta tehtävää.",
  },
];

export const Route = createFileRoute("/lelulaatikko")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: LelulaatikkoPage,
});

function LelulaatikkoPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Lelusäilytys"
      title={H1}
      intro="Lelulaatikko lapsen korkeudella muuttaa siivoamisen osaksi leikkiä: taapero yltää itse sekä hakemaan että palauttamaan lelunsa. Puinen lelulaatikko ja lelukori kestävät vuosia ja näyttävät hyvältä myös olohuoneessa."
      kauppaCategory="storage"
      match={(t, vendor, tags) => {
        const hay = `${t} ${vendor ?? ""} ${(tags ?? []).join(" ")}`.toLowerCase();
        return /(lelulaatikko|lelukori|leluarkku|toy\s*box|toy\s*chest|storage\s*box|säilytyslaatikko|säilytyskori)/i.test(
          hay,
        );
      }}
      relatedChips={[
        { label: "Vaaterekki", to: "/vaaterekki" },
        { label: "Kaikki säilytys", to: "/kauppa" },
        { label: "Montessori-lelut", to: "/montessori-lelut" },
        { label: "Kalusteopas", to: "/montessori-koulu/montessori-kalusteet-kotiin" },
      ]}
      body={[
        {
          heading: "Miksi lelulaatikko kuuluu lapsen korkeudelle",
          paragraphs: [
            "Aikuisten säilytys on lapselta piilossa: korkeat hyllyt, raskaat ovat ja laatikot, joita ei saa avata. Kun lelulaatikko on lattiatasolla ja kevyt käyttää, siivoaminen ei ole aikuisen tehtävä vaan luonnollinen loppu jokaiselle leikille.",
            "Käytännössä jo 2-vuotias oppii rutiinin: leikki loppuu, lelut palavat kotiin laatikkoonsa. Rutiini kasvattaa vastuunkantoa — ja lastenhuone pysyy siistinä ilman jatkuvaa siivousta.",
          ],
        },
        {
          heading: "Lelulaatikko, lelukori vai avohylly?",
          paragraphs: [
            "Lelulaatikko on paras yleissäilytys: iso määrä leluja poissa näkyvistä yhdessä paikassa. Lelukori puolestaan liikkuu mukana — lasta viedään olohuoneeseen päiväksi ja takaisin illaksi.",
            "Montessori-perheissä suosittu yhdistelmä on avohylly + lelulaatikko: hyllyllä pyörii 6–8 viikon suosikkia selkeästi esillä ja loput lelut odottavat laatikossa vuoroaan. Näin lelut pysyvät kiinnostavina ja määrä hallittuna.",
          ],
        },
        {
          heading: "Mihin kiinnittää huomiota lelulaatikkoa ostaessa",
          paragraphs: [
            "Turvallisuus: valitse malli, jossa ei ole raskasta, sormia puristavaa kantta. Avoin tai kevyt kansi ja pyöreät kulmat ovat taaperon kannalta paras ratkaisu. Pintakäsittelyn tulee olla lapsiturvallinen (EN71).",
            "Materiaali: koivupuu ja koivuvaneri kestävät vuosien käyttöä ja näyttävät hyvältä koko kodissa. Muovilaatikot naarmuuntuvat ja halkeavat — puinen lelulaatikko siirtyy usein jo seuraavalle lapselle.",
            "Koko ja sijoittelu: mittaa paikka ennen ostoa. 50–80 cm leveä laatikko mahtuu useimpiin lastenhuoneisiin, ja penkkimäinen malli toimii samalla istumapaikkana.",
          ],
        },
      ]}
      faqs={FAQS}
    />
  );
}